import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../components/ThemeContext';
import { useResponsive } from '../components/useResponsive';
import HomeHeader, { firstNameFromEmail } from '../components/HomeHeader';

// ============================================================
// ROOT LANDING — workspace chooser
// Greeting + two workspace containers (Supply Chain, Investigations),
// each with Create / Manage / Analyze tiles. Supply Chain Create and
// Manage open auto.thetradespy.ai in a new tab; Analyze opens the
// /supplychain intelligence launcher. Investigations tiles are inert
// placeholders until link targets are defined.
// ============================================================

interface LandingUser {
  email: string;
  is_admin: boolean;
}

interface HomeLandingProps {
  user: LandingUser;
  onLogout: () => void;
  totalProducts: number;
}

const SERIF = "'Playfair Display', Georgia, serif";
const MONO = "'IBM Plex Mono', 'Courier New', monospace";
const SANS = "'Inter', -apple-system, sans-serif";

const AUTO_CREATE_URL = 'https://auto.thetradespy.ai/#/product/create';
const AUTO_MANAGE_URL = 'https://auto.thetradespy.ai/#/product/manage';

function greetingForNow(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

const IconPlus = ({ color }: { color: string }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 5v14" /><path d="M5 12h14" />
  </svg>
);

const IconSliders = ({ color }: { color: string }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 21v-7" /><path d="M4 10V3" /><path d="M12 21v-9" /><path d="M12 8V3" /><path d="M20 21v-5" /><path d="M20 12V3" /><path d="M1 14h6" /><path d="M9 8h6" /><path d="M17 16h6" />
  </svg>
);

const IconChart = ({ color }: { color: string }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 3v18h18" /><path d="M7 15v3" /><path d="M12 10v8" /><path d="M17 6v12" />
  </svg>
);

const IconFolder = ({ color }: { color: string }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
  </svg>
);

const IconFingerprint = ({ color }: { color: string }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 4" /><path d="M14 13.12c0 2.38 0 6.38-1 8.88" /><path d="M17.29 21.02c.12-.6.43-2.3.5-3.02" /><path d="M2 12a10 10 0 0 1 18-6" /><path d="M2 16h.01" /><path d="M21.8 16c.2-2 .131-5.354 0-6" /><path d="M5 19.5C5.5 18 6 15 6 12a6 6 0 0 1 .34-2" /><path d="M8.65 22c.21-.66.45-1.32.57-2" /><path d="M9 6.8a6 6 0 0 1 9 5.2v2" />
  </svg>
);

const IconUserCircle = ({ color }: { color: string }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><circle cx="12" cy="10" r="3" /><path d="M7 20.66V19a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v1.66" />
  </svg>
);

const IconShieldCheck = ({ color }: { color: string }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" /><path d="m9 12 2 2 4-4" />
  </svg>
);

const IconGauge = ({ color }: { color: string }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="m12 14 4-4" /><path d="M3.34 19a10 10 0 1 1 17.32 0" />
  </svg>
);

const IconSignOut = ({ color }: { color: string }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

interface WorkspaceTileDef {
  id: string;
  title: string;
  caption: string;
  desc: string;
  icon: React.ReactNode;
  externalUrl?: string;
  path?: string;
  onClick?: () => void;
}

export default function HomeLanding({ user, onLogout, totalProducts }: HomeLandingProps) {
  const { T } = useTheme();
  const { isMobile } = useResponsive();
  const navigate = useNavigate();

  const nf = new Intl.NumberFormat('en-US');
  const ndcCount = totalProducts > 0 ? nf.format(totalProducts) : '149,000+';

  const supplyChainTiles: WorkspaceTileDef[] = [
    {
      id: 'sc-create', title: 'Create', caption: 'PRODUCT ARCHITECT · AUTO',
      desc: 'Start a new product in a disposable draft workspace. Nothing goes live until you promote it.',
      icon: <IconPlus color={T.red} />, externalUrl: AUTO_CREATE_URL,
    },
    {
      id: 'sc-manage', title: 'Manage', caption: 'CATALOG · AUTO',
      desc: 'Manage product collections, drafts, and operational workflow contracts.',
      icon: <IconSliders color={T.red} />, externalUrl: AUTO_MANAGE_URL,
    },
    {
      id: 'sc-analyze', title: 'Analyze', caption: `INTELLIGENCE · ${ndcCount} NDCs`,
      desc: 'Supply-chain intelligence: dashboard, risk analyzer, briefings, shortages, and CCP exposure.',
      icon: <IconChart color={T.red} />, path: '/supplychain',
    },
  ];

  const investigationTiles: WorkspaceTileDef[] = [
    {
      id: 'inv-create', title: 'Create', caption: 'NEW CASE FILE',
      desc: 'Open a new investigation case file. Destination to be defined.',
      icon: <IconPlus color={T.red} />,
    },
    {
      id: 'inv-manage', title: 'Manage', caption: 'OPEN CASES',
      desc: 'Track open case files, evidence, and assigned analysts. Destination to be defined.',
      icon: <IconFolder color={T.red} />,
    },
    {
      id: 'inv-analyze', title: 'Analyze', caption: 'CASE FINDINGS',
      desc: 'Run analysis across investigation findings. Destination to be defined.',
      icon: <IconFingerprint color={T.red} />,
    },
  ];

  const accountTiles: WorkspaceTileDef[] = [
    {
      id: 'acct-my-account', title: 'My Account', caption: 'PROFILE · SETTINGS',
      desc: 'Manage your profile, credentials, and subscription details.',
      icon: <IconUserCircle color={T.red} />, path: '/my-account',
    },
    {
      id: 'acct-attestations', title: 'My Attestations', caption: 'SUBMISSIONS',
      desc: 'Review the self-attestations you have submitted and their status.',
      icon: <IconShieldCheck color={T.red} />, path: '/my-attestations',
    },
    ...(user.is_admin ? [{
      id: 'acct-admin', title: 'Admin Dashboard', caption: 'ADMINISTRATION',
      desc: 'User management, subscriptions, and platform administration.',
      icon: <IconGauge color={T.red} />, path: '/admin',
    }] : []),
    {
      id: 'acct-sign-out', title: 'Sign Out', caption: 'END SESSION',
      desc: 'Sign out of TheTradeSpy.ai and return to the content site.',
      icon: <IconSignOut color={T.red} />, onClick: onLogout,
    },
  ];

  const tileInner = (tile: WorkspaceTileDef, external: boolean) => (
    <>
      <span style={{ display: 'block' }}>{tile.icon}</span>
      <div style={{ marginTop: 16, fontFamily: SERIF, fontSize: 19, fontWeight: 700, color: T.ink, lineHeight: 1.15 }}>
        {tile.title}
      </div>
      <div style={{ marginTop: 8, fontFamily: MONO, fontSize: 10.5, fontWeight: 600, letterSpacing: '0.1em', color: T.red, textTransform: 'uppercase' }}>
        {tile.caption}
      </div>
      <div style={{ marginTop: 8, fontFamily: SANS, fontSize: 13, color: T.textMid, lineHeight: 1.5 }}>
        {tile.desc}
      </div>
      {external && (
        <span style={{
          fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.1em', color: T.textLight,
          position: 'absolute', bottom: 14, right: 16,
        }}>
          EXTERNAL ↗
        </span>
      )}
    </>
  );

  const tileStyle: React.CSSProperties = {
    display: 'block', textAlign: 'left', textDecoration: 'none',
    background: T.surfaceCard, border: `1px solid ${T.borderCard}`,
    borderRadius: T.radius, boxShadow: T.shadowRest,
    padding: '18px 18px 20px', minHeight: 172, position: 'relative',
    transition: 'transform .16s ease, box-shadow .16s ease',
  };

  const renderTile = (tile: WorkspaceTileDef) => {
    if (tile.externalUrl) {
      return (
        <a
          key={tile.id}
          className="hl-tile"
          href={tile.externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ ...tileStyle, cursor: 'pointer', color: 'inherit' }}
        >
          {tileInner(tile, true)}
        </a>
      );
    }
    if (tile.path || tile.onClick) {
      const action = tile.onClick ?? (() => navigate(tile.path as string));
      return (
        <button
          key={tile.id}
          className="hl-tile"
          onClick={action}
          style={{ ...tileStyle, cursor: 'pointer', width: '100%' }}
        >
          {tileInner(tile, false)}
        </button>
      );
    }
    return (
      <div key={tile.id} className="hl-tile-disabled" style={{ ...tileStyle, opacity: 0.72 }}>
        {tileInner(tile, false)}
      </div>
    );
  };

  const renderWorkspace = (
    label: string,
    subLabel: string,
    tiles: WorkspaceTileDef[],
  ) => (
    <div style={{
      background: 'rgba(252,251,249,0.55)', border: `1px solid ${T.borderCard}`,
      borderRadius: T.radius, padding: isMobile ? '16px 16px 20px' : '22px 22px 26px',
      marginTop: 22,
    }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        gap: 16, flexWrap: 'wrap',
        borderBottom: `1px solid ${T.borderCard}`, paddingBottom: 14,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 10, height: 10, background: T.red }} />
          <span style={{
            fontFamily: MONO, fontWeight: 600, fontSize: 13, letterSpacing: '0.15em',
            color: T.ink, textTransform: 'uppercase',
          }}>
            {label}
          </span>
        </div>
        <span style={{ fontFamily: MONO, fontSize: 10.5, letterSpacing: '0.13em', color: T.textLight }}>
          {subLabel}
        </span>
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
        gap: 16, marginTop: 18,
      }}>
        {tiles.map(renderTile)}
      </div>
    </div>
  );

  return (
    <div style={{ background: T.surfacePage, minHeight: '100%' }}>
      <style>{`
        .hl-tile:hover { box-shadow: ${T.shadowHover}; transform: translateY(-3px); }
        @media (prefers-reduced-motion: reduce) {
          .hl-tile:hover { transform: none; }
        }
      `}</style>

      <HomeHeader user={user} onLogout={onLogout} />

      {/* Body */}
      <div style={{ maxWidth: 1120, margin: '0 auto', padding: isMobile ? '28px 20px 56px' : '44px 40px 72px' }}>

        {/* Decorative mark */}
        <div style={{ width: 34, height: 4, borderRadius: 2, background: T.red, margin: '0 auto 22px' }} />

        {/* Hero */}
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <h1 style={{
            fontFamily: SERIF, fontWeight: 700,
            fontSize: isMobile ? 30 : 40, color: T.ink,
            margin: 0, letterSpacing: '-0.01em',
          }}>
            {greetingForNow()}, {firstNameFromEmail(user.email)}
          </h1>
          <div style={{ fontFamily: SANS, fontSize: 14.5, lineHeight: 1.6, color: T.textMid, marginTop: 14 }}>
            Choose a workspace to enter.
          </div>
        </div>

        {renderWorkspace('Supply Chain', 'PRODUCT OPERATIONS · NDC INTELLIGENCE', supplyChainTiles)}

        {renderWorkspace('Investigations', 'CASE WORK · COMING ONLINE', investigationTiles)}

        {renderWorkspace('Account', 'PROFILE · SESSION', accountTiles)}
      </div>
    </div>
  );
}
